import { useAuth } from "../../context/AuthContext";
import {
  accessLabel,
  ROLE_LABELS,
  rolesForAccess,
  type RoleId,
} from "../../lib/access";
import {
  buildUiOutline,
  ROLE_DESCRIPTIONS,
  UI_PANEL_OUTLINE,
  type UiOutlineItem,
} from "../../registry/uiOutline";

function AccessBadge({ access }: { access: UiOutlineItem["access"] }) {
  const isAdmin = access === "admin";
  return (
    <span
      className={[
        "shrink-0 rounded px-1.5 py-0.5 font-medium",
        isAdmin
          ? "bg-status-warn/15 text-status-warn"
          : "bg-status-ok/10 text-status-ok",
      ].join(" ")}
      style={{ fontSize: "10px" }}
    >
      {accessLabel(access)}
    </span>
  );
}

function RoleChips({ roles }: { roles: RoleId[] }) {
  return (
    <span className="flex flex-wrap gap-1">
      {roles.map((r) => (
        <span
          key={r}
          className="rounded bg-page px-1.5 py-0.5 text-text-secondary"
          style={{ fontSize: "10px" }}
        >
          {ROLE_LABELS[r]}
        </span>
      ))}
    </span>
  );
}

function OutlineRow({ item }: { item: UiOutlineItem }) {
  const roles = rolesForAccess(item.access);
  return (
    <li className="rounded-lg border border-border-subtle px-3 py-2.5">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div
            className="font-medium text-text-primary"
            style={{ fontSize: "var(--font-body)" }}
          >
            {item.label}
          </div>
          {item.description && (
            <p className="mt-0.5 text-text-secondary" style={{ fontSize: "var(--font-micro)" }}>
              {item.description}
            </p>
          )}
        </div>
        <AccessBadge access={item.access} />
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <span className="text-text-tertiary" style={{ fontSize: "10px" }}>
          Roles:
        </span>
        <RoleChips roles={roles} />
      </div>
    </li>
  );
}

export function UiOutlineView() {
  const { user } = useAuth();
  const screens = buildUiOutline();

  return (
    <div className="space-y-6">
      <div>
        <h3
          className="font-semibold text-text-primary"
          style={{ fontSize: "var(--font-heading)" }}
        >
          UI map & access
        </h3>
        <p className="mt-1 text-text-secondary" style={{ fontSize: "var(--font-micro)" }}>
          Signed-in users can view almost everything. Only user management, the admin screen, and
          catalog sync are restricted to admins.
          {user && (
            <>
              {" "}
              You are signed in as <span className="font-medium text-text-primary">{user.login}</span>
              {user.roles.length ? ` (${user.roles.join(", ")})` : ""}.
            </>
          )}
        </p>
      </div>

      <section>
        <h4
          className="mb-2 font-semibold text-text-primary"
          style={{ fontSize: "var(--font-body)" }}
        >
          User categories
        </h4>
        <ul className="space-y-2">
          {ROLE_DESCRIPTIONS.map((r) => (
            <li
              key={r.role}
              className="rounded-lg border border-border-subtle bg-page px-3 py-2.5"
            >
              <span className="font-medium text-text-primary">{ROLE_LABELS[r.role]}</span>
              <p className="mt-0.5 text-text-secondary" style={{ fontSize: "var(--font-micro)" }}>
                {r.summary}
              </p>
            </li>
          ))}
        </ul>
      </section>

      <section>
        <h4
          className="mb-2 font-semibold text-text-primary"
          style={{ fontSize: "var(--font-body)" }}
        >
          Main screens
        </h4>
        <div className="space-y-4">
          {screens.map((screen) => (
            <div key={screen.id} className="rounded-lg border border-border-subtle">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border-subtle px-3 py-2.5">
                <div>
                  <span className="font-medium text-text-primary">{screen.label}</span>
                  <span className="ml-2 font-mono text-text-tertiary" style={{ fontSize: "10px" }}>
                    {screen.path}
                  </span>
                </div>
                <AccessBadge access={screen.access} />
              </div>
              {screen.cards.length > 0 ? (
                <ul className="divide-y divide-border-subtle">
                  {screen.cards.map((card) => (
                    <li key={card.id} className="px-3 py-2">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div>
                          <span style={{ fontSize: "var(--font-micro)" }}>{card.label}</span>
                          {card.description && (
                            <p className="text-text-tertiary" style={{ fontSize: "10px" }}>
                              {card.description}
                            </p>
                          )}
                        </div>
                        <AccessBadge access={card.access} />
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="px-3 py-2 text-text-tertiary" style={{ fontSize: "var(--font-micro)" }}>
                  No cards
                </p>
              )}
            </div>
          ))}
        </div>
      </section>

      {UI_PANEL_OUTLINE.map((section) => (
        <section key={section.id}>
          <h4
            className="mb-2 font-semibold text-text-primary"
            style={{ fontSize: "var(--font-body)" }}
          >
            {section.label}
          </h4>
          <ul className="space-y-2">
            {section.items.map((item) => (
              <OutlineRow key={item.id} item={item} />
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
